import React, {useState} from "react";

import {
    Autocomplete,
    Chip,
    FormControl,
    FormHelperText, IconButton, InputAdornment,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    TextField,
    Typography
} from "@mui/material";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";

import { de } from 'date-fns/locale';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import "../App.css"
import {CxIcon} from "./CxIcon";
import {DateUtil} from "../lib/DateUtil"
import {CxInfo} from "./CxTypography";
import {CxGrid} from "./CxGrid";
import {Visibility, VisibilityOff} from "@mui/icons-material";


// --------------------------------------------------------------------------------------------------------------------
// info
// --------------------------------------------------------------------------------------------------------------------

export const CxMetaInfo = ({values, pk='id'}) => {

    // capitalize the first letter of the label
    const label = pk.charAt(0).toUpperCase() + pk.slice(1);

    // create update info
    const updateInfo = values["update_by"] && values["update_ts"] ?
        `| Letzte Aktualisierung: ${values["update_by"]}, ${values["update_ts"]}` : ``;

    // show info (if pk is available)
    return (
        <>
            { values[pk] &&
                <CxGrid size="xl">
                    <CxInfo>{label}: {values[pk]} {updateInfo}</CxInfo>
                </CxGrid>
            }
        </>
    )
}

// --------------------------------------------------------------------------------------------------------------------
// input box
// --------------------------------------------------------------------------------------------------------------------

export const CxBox = ({label, children}) => {
    return (
        <Box sx={{ pt: 1, pb: 0.5 }}>
        <Box
            sx={{
                position: "relative",           // Allows absolute positioning of the label
                border: "1px solid rgba(0, 0, 0, 0.23)",
                borderRadius: 1,
                padding: 0.9,
                minWidth: '238px',
            }}
        >
            <Typography                         // Label positioned within the border
                variant="caption"
                sx={{
                    position: "absolute",
                    top: -10,                   // Adjusts label above the border
                    left: 10,                   // Aligns label with padding
                    backgroundColor: "white",   // Matches the container background
                    paddingX: 0.5,              // Adds slight padding around the text
                    color: "text.secondary"
                }}
            >
                {label}
            </Typography>
            {children}
        </Box>
        </Box>
    );
}

// --------------------------------------------------------------------------------------------------------------------
// input components
// --------------------------------------------------------------------------------------------------------------------

export const CxTextField = ({ field, label, maxLength, handleChange, formValues, formErrors,
                              numericOnly=false, readOnly=false }) => {
    return (
        <TextField
            id={field}
            name={field}
            label={label}
            variant="outlined"
            fullWidth
            margin="dense"
            value={formValues[field] || ""}
            onChange={(e) => {
                const { value } = e.target;
                if (
                    (!numericOnly || /^[0-9]*$/.test(value)) &&  // Check for numeric only
                    (!maxLength || value.length <= maxLength)    // Check for max length
                ) {
                    handleChange(e);
                }
            }}
            error={!!formErrors[field]}
            helperText={formErrors[field] || ""}
            disabled={readOnly}
        />
    );
}


export const CxPasswordField = ({ field, label, maxLength, handleChange, formValues, formErrors,
                                numericOnly=false, readOnly=false }) => {

    const [showPassword, setShowPassword] = useState(false);

    return (

        <TextField
            id={field}
            name={field}
            label={label}
            variant="outlined"
            fullWidth
            margin="dense"
            type={showPassword ? "text" : "password"}
            value={formValues[field] || ""}
            onChange={(e) => {
                const { value } = e.target;
                if (
                    (!numericOnly || /^[0-9]*$/.test(value)) &&  // Check for numeric only
                    (!maxLength || value.length <= maxLength)    // Check for max length
                ) {
                    handleChange(e);
                }
            }}
            error={!!formErrors[field]}
            helperText={formErrors[field] || ""}
            disabled={readOnly}
            slotProps={{
                input: {
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                aria-label="toggle password visibility"
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                            >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                        </InputAdornment>
                    ),
                },
            }}
        />
    );
}


export const CxLocalization = ({ children }) => {
    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
            {children}
        </LocalizationProvider>
    );
}

export const CxDatePicker = ({field, label, disablePast=true, readOnly=false,
                                 handleChange, formValues, formErrors}) => {
    return (
        <DatePicker
            disablePast={disablePast}
            label={label}
            value={ DateUtil.convertIsoStringToDate(formValues[field]) }
            onChange={ (newValue) => handleChange(field, newValue) }
            slotProps={{
                textField: {
                    readOnly: true,
                    fullWidth: true,
                    margin: "dense",
                    error: !!formErrors[field],
                    helperText: formErrors[field] || ""
                },
            }}
            disabled={readOnly}
        />
    );
}

export const CxDatePickerNative = ({id, field, label, disablePast=true, readOnly=false,
                                       handleChange, value, error}) => {
    return (
        <DatePicker
            key={"dp" + id}
            label={label}
            value={ DateUtil.convertIsoStringToDate(value) }
            onChange={ (newValue) => handleChange(id, field, newValue) }
            slotProps={{
                textField: {
                    readOnly: true,
                    fullWidth: true,
                    margin: "dense",
                    error: !!error,
                    helperText: error || ""
                },
            }}
            disabled={readOnly}
            disablePast={disablePast}
        />
    );
}


export const CxCheckBox = ({ field, label, readOnly = false, handleChange, formValues, formErrors }) => {
    return (
        <FormControl error={!!formErrors[field]}>
            <FormControlLabel
                label={label}
                name={field}
                control={
                    <Checkbox
                        checked={formValues[field] === 1} // Convert 1/0 back to boolean for MUI Checkbox
                        onChange={handleChange}
                        disabled={readOnly}
                    />
                }
            />
            {formErrors[field] && <FormHelperText>{formErrors[field]}</FormHelperText>}
        </FormControl>
    );
};

// --------------------------------------------------------------------------------------------------------------------
// selections
// --------------------------------------------------------------------------------------------------------------------

export function CxRadioboxSelection({label, name, value, onChange, items, readOnly=false}) {

    const handleChange = (event) => {
        const value = event.target.value;   // get new selection
        if (onChange) onChange(value);      // call the parent's handler if available
    };

    return (
        <CxBox label={label}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pl: 1}} >
                <FormControl>
                    <RadioGroup row name={name} value={value} onChange={handleChange} >
                        {items.map((item) => (
                            <FormControlLabel
                                key={item.value}
                                control={<Radio sx={{ pb: 0.8 }} disabled={readOnly} />}
                                value={item.value}
                                label={item.label} />
                        ))}
                    </RadioGroup>
                </FormControl>
            </Box>
        </CxBox>
    );
}


export function CxComboxSelection({id, name, value, readOnly=false, onChange, items, formErrors= {}}) {

    const handleChange = (event) => {
        const newValue = event.target.value;
        if (onChange) onChange(newValue);
    };

    const labelId = `${id}-label`;

    return (
        <FormControl fullWidth sx={{ mt: 1, mb: 0.5 }} error={!!formErrors[id]} >
            <InputLabel id={labelId}>{name}</InputLabel>
            <Select
                labelId={labelId}
                id={id}
                value={value ?? ""}         // wichtig für Reset und leere Werte
                label={name}
                onChange={handleChange}
                disabled={readOnly}
            >
                {items.map((item) => (
                    <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
                ))}
            </Select>
            <FormHelperText>{formErrors[id] || ""}</FormHelperText>
        </FormControl>
    );
};


export function CxChipSelection ({id, name, items, onChange}) {

    const handleDelete = (deleteId) => {
        const updatedItems = items.filter((item) => item.id !== deleteId);
        onChange(updatedItems.map((item) => item.id));  // send updated IDs to the handler
    };

    return (
        <CxBox label={name}>
            <Box id={id} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', margin:0.5 }}>
                {items.map(({ id, label }) => (
                    <Chip
                        key={id}
                        label={label}
                        onDelete={() => handleDelete(id)}
                        sx={{ fontSize: '14px' }}
                    />
                ))}
            </Box>
        </CxBox>
    );
};

export function CxAutocomplete({id, label, currentValue, items, onChange, error, readOnly = false, multiple=false}) {
    return (
        <Autocomplete
            key={id}
            options={items}
            value={currentValue}
            getOptionLabel={(option) => option.label}  // Display the label
            isOptionEqualToValue={(option, value) => option.value === value.value} // Ensure proper comparison
            onChange={onChange}
            disabled={readOnly}
            multiple={multiple}
            renderInput={(params) =>
                <TextField {...params}
                           label={label}
                           error={!!error}
                           helperText={error || ""}
                           fullWidth margin="dense" />
            }
        />
    );
};

// --------------------------------------------------------------------------------------------------------------------
// simple inputs (without form elements)
// --------------------------------------------------------------------------------------------------------------------

export const CxTextInput = ({ id, label, value, handleChange, maxLength, numericOnly=false }) => {
    return (
        <TextField
            id={id}
            name={id}
            label={label}
            variant="outlined"
            fullWidth
            margin="dense"
            value={value || ""}
            onChange={(e) => {
                const val = e.target.value;
                if (
                    (!numericOnly || /^[0-9]*$/.test(value)) &&  // Check for numeric only
                    (!maxLength || value.length <= maxLength)    // Check for max length
                ) {
                    handleChange(val);
                }
            }}
        />
    );
};

export const CxDateInput = ({id, label, value, handleChange}) => {
    return (
        <DatePicker
            id={ id }
            label={ label }
            value={ value }
            onChange={ (newValue) => handleChange(newValue) }
            slotProps={{
                textField: { fullWidth: true, margin: "dense"}
            }}
        />
    );
};


export function CxComboxInput({ id, name, value, onChange, items }) {
    return (
        <FormControl fullWidth sx={{ mt: 1, mb: 0.5 }}>
            <InputLabel id={`${id}-label`}>{name}</InputLabel>
            <Select
                labelId={`${id}-label`}
                id={id}
                value={value ?? ""} // Verhindert MUI Warnungen bei null/undefined
                label={name}
                onChange={(e) => onChange(e.target.value)}
            >
                {items.map((item) => (
                    <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};


// --------------------------------------------------------------------------------------------------------------------
// action buttons
// --------------------------------------------------------------------------------------------------------------------

export const CxButton = ({label, action, handleSubmit}) => {
    return (
        <Box display="flex" justifyContent="right" gap={1} sx={{ mt: 2 }}>
            <Button variant="contained" onClick={() => handleSubmit(action)}>
                {label}
            </Button>
        </Box>
    );
}

export const CxCloseButton = ({handleSubmit}) => {
    return (
        <Box display="flex" justifyContent="right" gap={1} sx={{ mt: 2 }}>
            <Button variant="contained" onClick={() => handleSubmit("close")}>
                Schliessen
            </Button>
        </Box>
    );
}

export const CxResetFilterButton = ({handleSubmit}) => {
    return (
        <Box display="flex" justifyContent="right" gap={1} sx={{ mt: 2 }}>
            <Button variant="outlined" onClick={() => handleSubmit("close")}>
                Reset Filter
            </Button>
        </Box>
    );
}

export const CxResetSearchButton = ({handleSubmit}) => {
    return (
        <Box display="flex" justifyContent="right" gap={1} sx={{ mt: 2 }}>
            <Button variant="outlined" onClick={() => handleSubmit("reset")}>
                Reset
            </Button>
            <Button variant="contained" onClick={() => handleSubmit("search")}>
                Suchen
            </Button>
        </Box>
    );
}

export const CxCancelSaveButton = ({handleSubmit}) => {
    return (
        <Box display="flex" justifyContent="right" gap={1} sx={{ mt: 2 }}>
            <Button variant="outlined" onClick={() => handleSubmit("cancel")}>
                Abbrechen
            </Button>
            <Button variant="contained" onClick={() => handleSubmit("save")}>
                Speichern
            </Button>
        </Box>
    );
}

export const CxDeleteCancelSaveButton = ({handleSubmit}) => {
    return (
        <Box display="flex" justifyContent="right" gap={1} sx={{ mt: 2 }}>
            <Button variant="outlined" color="error" startIcon={<CxIcon name="delete" />}
                    onClick={() => handleSubmit("delete")} >
                    Löschen
            </Button>
            <Button variant="outlined" onClick={() => handleSubmit("cancel")}>
                Abbrechen
            </Button>
            <Button variant="contained" onClick={() => handleSubmit("save")}>
                Speichern
            </Button>
        </Box>

    );
}
